/**
 * The review detail page's comments aside, grouped by the document version
 * each comment was anchored against: current-version comments first (their
 * line labels jump to the anchored block), then one "written against v{n}"
 * group per older version, newest first. Stale anchors keep the quoted text
 * as the recovery aid but get no jump link — their line numbers reference
 * replaced text.
 */
import { useState } from "react";

import type { AdminReviewComment } from "../../api/admin";
import { Markdown } from "../Markdown";
import { Pill } from "../Pill";

interface Props {
  comments: AdminReviewComment[];
  docVersion: number;
  reviewing: boolean;
  onEdit: (comment: AdminReviewComment, bodyMd: string) => void;
  onDelete: (comment: AdminReviewComment) => void;
  /** Jump to the block containing a current-version comment's first line. */
  onJump: (line: number) => void;
}

function isStale(c: AdminReviewComment, docVersion: number): boolean {
  return c.stale ?? c.content_version < docVersion;
}

export function ReviewComments({
  comments,
  docVersion,
  reviewing,
  onEdit,
  onDelete,
  onJump,
}: Props) {
  const current = comments.filter((c) => !isStale(c, docVersion));
  const stale = comments.filter((c) => isStale(c, docVersion));
  const staleVersions = [...new Set(stale.map((c) => c.content_version))].sort(
    (a, b) => b - a,
  );

  return (
    <div className="space-y-4">
      {current.length > 0 ? (
        <ul className="space-y-3">
          {current.map((c) => (
            <CommentCard
              key={c.id}
              comment={c}
              stale={false}
              editable={reviewing && c.state === "draft"}
              onEdit={(bodyMd) => onEdit(c, bodyMd)}
              onDelete={() => onDelete(c)}
              onJump={c.line_start > 0 ? () => onJump(c.line_start) : undefined}
            />
          ))}
        </ul>
      ) : null}

      {staleVersions.map((v) => (
        <div key={v}>
          <div className="font-mono text-xs text-ink-low mb-2">
            written against v{v}
          </div>
          <ul className="space-y-3">
            {stale
              .filter((c) => c.content_version === v)
              .map((c) => (
                <CommentCard
                  key={c.id}
                  comment={c}
                  stale
                  editable={reviewing && c.state === "draft"}
                  onEdit={(bodyMd) => onEdit(c, bodyMd)}
                  onDelete={() => onDelete(c)}
                />
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function CommentCard({
  comment,
  stale,
  editable,
  onEdit,
  onDelete,
  onJump,
}: {
  comment: AdminReviewComment;
  stale: boolean;
  editable: boolean;
  onEdit: (bodyMd: string) => void;
  onDelete: () => void;
  onJump?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(comment.body_md);
  const lines =
    comment.line_start === 0 ? "General feedback" : comment.line_end !== comment.line_start
      ? `L${comment.line_start}–L${comment.line_end}`
      : `L${comment.line_start}`;

  return (
    <li className="border border-rule rounded p-3">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {onJump ? (
          <button
            type="button"
            className="font-mono text-xs text-ink-low tnum link-quiet"
            title="Jump to the anchored block"
            onClick={onJump}
          >
            {lines}
          </button>
        ) : (
          <span className="font-mono text-xs text-ink-low tnum">{lines}</span>
        )}
        <Pill
          value={comment.state}
          tone={comment.state === "draft" ? "signal" : "muted"}
        />
        {stale ? (
          <Pill value={`anchored to v${comment.content_version}`} tone="alert" />
        ) : null}
      </div>
      {comment.quoted_text ? (
        <blockquote className="font-mono text-xs text-ink-low border-l-2 border-rule pl-2 mb-2 whitespace-pre-wrap max-h-24 overflow-y-auto">
          {comment.quoted_text}
        </blockquote>
      ) : null}
      {editing ? (
        <>
          <textarea
            className="input w-full"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-sm btn-main"
              disabled={!text.trim()}
              onClick={() => {
                onEdit(text);
                setEditing(false);
              }}
            >
              Save
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                setText(comment.body_md);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <Markdown text={comment.body_md} />
          {editable ? (
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={onDelete}
              >
                Discard
              </button>
            </div>
          ) : null}
        </>
      )}
    </li>
  );
}
